import {
  ChannelRuleDto,
  CheckVerdict,
  ContactStatus,
  EngagementDto,
  FitTier,
  LeadEventDto,
  LeadPersonDto,
  LeadStage,
  SenderDto,
  SendingSettingsDto,
} from "@growth/dto";
import { Badge, Button, cn } from "@growth/ui";
import {
  Ban,
  Check,
  CheckCheck,
  CircleAlert,
  CircleCheck,
  CircleHelp,
  CircleX,
  FlaskConical,
  Globe,
  type LucideIcon,
  Mail,
  MailOpen,
  MousePointerClick,
  Phone,
  Reply,
  RotateCcw,
  Send,
  StickyNote,
  TriangleAlert,
  UserCheck,
  UserPlus,
  UsersRound,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { Link } from "react-router-dom";

import { formatRelative } from "../../lib/format";
import { useResetSample } from "../../lib/leads";
import { type SendCheck } from "../../lib/leads-sample";

export const card = "rounded-xl border border-line bg-white";

/** Says plainly that these screens run on sample data. */
export const SampleBanner = () => {
  const reset = useResetSample();
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-dashed border-amber-300 bg-amber-50 px-4 py-2 text-xs text-amber-900">
      <span className="flex items-center gap-2">
        <FlaskConical className="size-4" />
        Accounts, people and outreach are sample data for now: nothing here is saved to the server
        and no email leaves the platform. The ideal customer profile is real.
      </span>
      <button
        type="button"
        onClick={reset}
        className="flex items-center gap-1 font-medium underline"
      >
        <RotateCcw className="size-3" /> Reset the sample
      </button>
    </div>
  );
};

// ─── Fit ─────────────────────────────────────────────────────────────────────────────────────

const TIER_STYLE: Record<FitTier, string> = {
  A: "bg-ink text-white",
  B: "bg-accent text-white",
  C: "bg-blue-100 text-accent",
  D: "bg-paper text-muted ring-1 ring-line",
};

export const TierBadge = ({ tier, className }: { tier: FitTier; className?: string }) => (
  <span
    aria-label={`Fit tier ${tier}`}
    className={cn(
      "inline-grid size-6 place-items-center rounded-md font-serif text-sm font-semibold",
      TIER_STYLE[tier],
      className,
    )}
  >
    {tier}
  </span>
);

export const FitBar = ({ score, max }: { score: number; max: number }) => {
  const percent = max ? Math.round((score / max) * 100) : 0;
  return (
    <span className="flex items-center gap-2" title={`${score} of ${max} points`}>
      <span className="h-1.5 w-16 overflow-hidden rounded-full bg-paper">
        <span className="block h-full rounded-full bg-ink" style={{ width: `${percent}%` }} />
      </span>
      <span className="text-xs tabular-nums text-muted">
        {score}/{max}
      </span>
    </span>
  );
};

const VERDICT: Record<CheckVerdict, { icon: LucideIcon; className: string; label: string }> = {
  pass: { icon: CircleCheck, className: "text-emerald-600", label: "Pass" },
  unknown: { icon: CircleHelp, className: "text-amber-600", label: "Unknown" },
  fail: { icon: CircleX, className: "text-red-600", label: "Fail" },
};

export const VerdictIcon = ({ verdict }: { verdict: CheckVerdict }) => {
  const { icon: Icon, className, label } = VERDICT[verdict];
  return <Icon className={cn("size-4 shrink-0", className)} aria-label={label} />;
};

export const CheckDots = ({
  counts,
}: {
  counts: { pass: number; unknown: number; fail: number };
}) => (
  <span
    className="flex items-center gap-0.5"
    aria-label={`${counts.pass} pass, ${counts.unknown} unknown, ${counts.fail} fail`}
  >
    {(["pass", "unknown", "fail"] as const).flatMap((verdict) =>
      Array.from({ length: counts[verdict] }, (_, index) => (
        <span
          key={`${verdict}-${index}`}
          className={cn(
            "size-2 rounded-full",
            verdict === "pass"
              ? "bg-emerald-500"
              : verdict === "unknown"
                ? "bg-amber-400"
                : "bg-red-500",
          )}
        />
      )),
    )}
  </span>
);

// ─── People ──────────────────────────────────────────────────────────────────────────────────

export const CONTACT_STATUS: Record<
  ContactStatus,
  { label: string; className: string; about: string }
> = {
  published: {
    label: "Published",
    className: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    about: "The account publishes this address for the person or role.",
  },
  pattern_verified: {
    label: "Exists, not published",
    className: "bg-amber-50 text-amber-800 ring-amber-200",
    about: "Built from the account's address pattern and verified as deliverable.",
  },
  guessed: {
    label: "Guessed",
    className: "bg-amber-50 text-amber-800 ring-amber-200",
    about: "Built from the account's pattern; not verified.",
  },
  none: { label: "No email", className: "", about: "No address found." },
};

export const ContactStatusBadge = ({ status }: { status: ContactStatus }) => (
  <Badge className={CONTACT_STATUS[status].className} title={CONTACT_STATUS[status].about}>
    {CONTACT_STATUS[status].label}
  </Badge>
);

export const STAGES: { id: LeadStage; label: string }[] = [
  { id: "new", label: "New" },
  { id: "contacted", label: "Contacted" },
  { id: "engaged", label: "Engaged" },
  { id: "replied", label: "Replied" },
  { id: "meeting", label: "Meeting booked" },
  { id: "won", label: "Won" },
  { id: "lost", label: "Lost" },
  { id: "not_now", label: "Not now" },
];
export const stageLabel = (stage: LeadStage) => STAGES.find((item) => item.id === stage)?.label;

export const ROLE_LABEL: Record<LeadPersonDto["role"], string> = {
  budget_holder: "Budget holder",
  champion: "Champion",
  user: "User",
  other: "Other",
};

export const BEST_CHANNEL: Record<
  LeadPersonDto["bestChannel"],
  { label: string; icon: LucideIcon }
> = {
  email: { label: "Email", icon: Mail },
  linkedin: { label: "LinkedIn, by hand", icon: UserPlus },
  phone: { label: "Phone", icon: Phone },
  team_inbox: { label: "Team inbox", icon: UsersRound },
  none: { label: "No route yet", icon: Ban },
};

export const BestChannel = ({ channel }: { channel: LeadPersonDto["bestChannel"] }) => {
  const { label, icon: Icon } = BEST_CHANNEL[channel];
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted">
      <Icon className="size-3.5" /> {label}
    </span>
  );
};

const CHANNEL_LABEL: Record<ChannelRuleDto["channel"], { label: string; icon: LucideIcon }> = {
  email: { label: "Email", icon: Mail },
  phone: { label: "Phone", icon: Phone },
  linkedin: { label: "LinkedIn", icon: UserPlus },
};

/** Each channel, whether we may use it, and why. */
export const ChannelRules = ({ channels }: { channels: ChannelRuleDto[] }) => (
  <ul className="divide-y divide-line">
    {channels.map((rule) => {
      const { label, icon: Icon } = CHANNEL_LABEL[rule.channel];
      return (
        <li key={rule.channel} className="flex gap-3 py-2.5">
          <Icon className="mt-0.5 size-4 shrink-0 text-muted" />
          <div className="min-w-0 flex-1">
            <p className="flex items-center gap-2 text-sm font-medium text-ink">
              {label}
              {rule.allowed ? (
                <Badge tone="success">Allowed</Badge>
              ) : (
                <Badge className="bg-red-50 text-red-700 ring-red-200">Not allowed</Badge>
              )}
            </p>
            <p className="text-xs leading-relaxed text-muted">{rule.basis}</p>
          </div>
        </li>
      );
    })}
  </ul>
);

// ─── Engagement ──────────────────────────────────────────────────────────────────────────────

export const EngagementStrip = ({
  engagement,
  scoring,
  compact = false,
}: {
  engagement: EngagementDto;
  scoring?: SendingSettingsDto["scoring"];
  compact?: boolean;
}) => {
  const items = [
    { label: "opens", value: engagement.opens, points: scoring?.open },
    { label: "clicks", value: engagement.clicks, points: scoring?.click },
    { label: "visits", value: engagement.visits, points: scoring?.visit },
    { label: "replies", value: engagement.replies, points: scoring?.reply },
  ];
  if (compact)
    return (
      <span className="text-xs tabular-nums text-muted">
        {items.map((item) => `${item.value} ${item.label}`).join(" · ")}
      </span>
    );
  return (
    <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted">Score</p>
        <p className="font-serif text-4xl tabular-nums text-ink">{engagement.score}</p>
      </div>
      {items.map((item) => (
        <div key={item.label}>
          <p className="text-xl tabular-nums text-ink">{item.value}</p>
          <p className="text-xs text-muted">
            {item.label}
            {scoring && (
              <span className="ml-1 text-[11px]">
                ({item.points == null ? "no points yet" : `+${item.points}`})
              </span>
            )}
          </p>
        </div>
      ))}
    </div>
  );
};

const EVENT: Record<LeadEventDto["kind"], { label: string; icon: LucideIcon; tone: string }> = {
  sent: { label: "Sent", icon: Send, tone: "text-ink" },
  delivered: { label: "Delivered", icon: CheckCheck, tone: "text-muted" },
  opened: { label: "Opened", icon: MailOpen, tone: "text-accent" },
  clicked: { label: "Clicked", icon: MousePointerClick, tone: "text-accent" },
  visited: { label: "Visited the site", icon: Globe, tone: "text-emerald-700" },
  replied: { label: "Replied", icon: Reply, tone: "text-emerald-700" },
  bounced: { label: "Bounced", icon: TriangleAlert, tone: "text-red-600" },
  unsubscribed: { label: "Unsubscribed", icon: Ban, tone: "text-red-600" },
  linkedin_sent: { label: "LinkedIn request", icon: UserPlus, tone: "text-ink" },
  linkedin_accepted: { label: "LinkedIn accepted", icon: UserCheck, tone: "text-emerald-700" },
  note: { label: "Note", icon: StickyNote, tone: "text-muted" },
};

const time = (iso: string) =>
  new Date(iso).toLocaleString("en-AU", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });

/** What happened, newest first. Machine events are hidden unless asked for; they never score. */
export const Timeline = ({
  events,
  scoring,
}: {
  events: LeadEventDto[];
  scoring?: SendingSettingsDto["scoring"];
}) => {
  const [showMachine, setShowMachine] = useState(false);
  const machine = events.filter((item) => item.machine).length;
  const shown = showMachine ? events : events.filter((item) => !item.machine);
  const points = (item: LeadEventDto) => {
    if (item.machine || !scoring) return null;
    const value =
      item.kind === "opened"
        ? scoring.open
        : item.kind === "clicked"
          ? scoring.click
          : item.kind === "visited"
            ? scoring.visit
            : item.kind === "replied"
              ? scoring.reply
              : null;
    return value ? `+${value}` : null;
  };
  if (!events.length) return <p className="text-sm text-muted">Nothing yet.</p>;
  return (
    <div className="space-y-2">
      <ol className="space-y-0">
        {shown.map((item) => {
          const { label, icon: Icon, tone } = EVENT[item.kind];
          return (
            <li
              key={item.id}
              className={cn(
                "grid grid-cols-[1.25rem_minmax(0,1fr)_auto] gap-3 border-b border-line py-2.5 last:border-0",
                item.machine && "opacity-60",
              )}
            >
              <Icon className={cn("mt-0.5 size-4", item.machine ? "text-muted" : tone)} />
              <div className="min-w-0">
                <p className="text-sm text-ink">
                  <span className="font-medium">{label}</span>
                  {item.machine && (
                    <Badge className="ml-2 align-middle bg-paper">machine, not counted</Badge>
                  )}
                </p>
                <p className="break-words text-xs text-muted">
                  {item.detail}
                  {item.machineReason && <> · {item.machineReason}</>}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted">{time(item.at)}</p>
                {points(item) && (
                  <p className="text-xs font-medium tabular-nums text-ink">{points(item)}</p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
      {machine > 0 && (
        <button
          type="button"
          onClick={() => setShowMachine(!showMachine)}
          className="text-xs text-accent underline"
        >
          {showMachine
            ? "Hide machine events"
            : `Show ${machine} machine event${machine === 1 ? "" : "s"} (mail scanners and privacy proxies)`}
        </button>
      )}
    </div>
  );
};

// ─── Email ───────────────────────────────────────────────────────────────────────────────────

/** How the email will look: plain, left-aligned, signature and one-line opt-out. */
export const EmailPreview = ({
  sender,
  to,
  subject,
  body,
}: {
  sender: SenderDto | undefined;
  to: string | null;
  subject: string;
  body: string;
}) => (
  <div className="overflow-hidden rounded-xl border border-line bg-white">
    <dl className="grid grid-cols-[4rem_minmax(0,1fr)] gap-x-3 gap-y-1 border-b border-line bg-paper/60 px-4 py-3 text-xs">
      <dt className="text-muted">From</dt>
      <dd className="truncate text-ink">
        {sender ? `${sender.name} <${sender.email}>` : "No sender chosen"}
      </dd>
      <dt className="text-muted">To</dt>
      <dd className="truncate text-ink">{to ?? "No address"}</dd>
      <dt className="text-muted">Subject</dt>
      <dd className="truncate font-medium text-ink">{subject || "(no subject)"}</dd>
    </dl>
    <div
      className="space-y-3 px-5 py-4 text-[14px] leading-[1.55] text-[#222]"
      style={{ fontFamily: "Arial, Helvetica, sans-serif" }}
    >
      {body
        .split(/\n{2,}/)
        .filter((paragraph) => paragraph.trim())
        .map((paragraph, index) => (
          <p key={index} className="whitespace-pre-line">
            {paragraph}
          </p>
        ))}
      {sender && (
        <>
          <p className="whitespace-pre-line pt-1">{sender.signature}</p>
          <p className="border-t border-[#eee] pt-2 text-[12px] text-[#777]">{sender.optOutLine}</p>
        </>
      )}
    </div>
  </div>
);

const CHECK_ICON = {
  pass: { icon: Check, className: "text-emerald-600" },
  warn: { icon: CircleAlert, className: "text-amber-600" },
  fail: { icon: CircleX, className: "text-red-600" },
} as const;

export const SendChecks = ({ checks }: { checks: SendCheck[] }) => (
  <ul className="space-y-2">
    {checks.map((item) => {
      const { icon: Icon, className } = CHECK_ICON[item.state];
      return (
        <li key={item.id} className="flex gap-2">
          <Icon className={cn("mt-0.5 size-4 shrink-0", className)} aria-label={item.state} />
          <div className="min-w-0">
            <p className="text-sm text-ink">{item.label}</p>
            <p className="text-xs leading-relaxed text-muted">{item.detail}</p>
          </div>
        </li>
      );
    })}
  </ul>
);

// ─── Layout bits ─────────────────────────────────────────────────────────────────────────────

export const Panel = ({
  title,
  about,
  actions,
  children,
  className,
}: {
  title: string;
  about?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) => (
  <section className={cn(card, "space-y-3 p-5", className)}>
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div>
        <h2 className="font-serif text-xl text-ink">{title}</h2>
        {about && <p className="text-xs text-muted">{about}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
    {children}
  </section>
);

/** A friendly stop sign for steps that need an earlier step done. */
export const Gate = ({
  title,
  children,
  to,
  action,
}: {
  title: string;
  children: ReactNode;
  to: string;
  action: string;
}) => (
  <div className={cn(card, "space-y-3 p-6")}>
    <h2 className="font-serif text-xl text-ink">{title}</h2>
    <div className="max-w-xl text-sm text-muted">{children}</div>
    <Button asChild>
      <Link to={to}>{action}</Link>
    </Button>
  </div>
);

export const when = (iso: string | null) => (iso ? formatRelative(iso) : "never");
